class StoreGrid {
  constructor(sectionId, config) {
    this.sectionId = sectionId;
    this.config = config;
    this.allStores = [];
    this.filteredStores = [];
    this.selectedProvinces = [];
    this.searchTerm = "";
    this.preloader = document.getElementById(`preloader-${sectionId}`);
    this.wrapper = document.querySelector(
      `#store-grid-${sectionId} .store-grid-wrapper`
    );
    this.currentPage = 1;
    this.storesPerPage = 52;

    this.init();
  }

  formatStore(store) {
    return {
      ...store,
      id:
        store.id ||
        store._id ||
        `manual_${Math.random().toString(36).substr(2, 9)}`,
      latitude: parseFloat(store.latitude),
      longitude: parseFloat(store.longitude),
      operating_hours: store.operating_hours || store.operatingHours || {},
    };
  }

  formatOperatingHours(hours) {
    if (!hours || Object.keys(hours).length === 0) return "Hours not available";

    const daysOrder = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];

    const formatTime = (timeStr) => {
      if (!timeStr) return "";
      return timeStr.includes(":") ? timeStr.slice(0, 5) : "";
    };

    // Group consecutive days with same hours
    const groupedHours = [];
    let currentGroup = null;

    daysOrder.forEach((day) => {
      const dayHours = hours[day];
      if (!dayHours || !dayHours.open || !dayHours.close) {
        if (currentGroup) {
          groupedHours.push(currentGroup);
          currentGroup = null;
        }
        return;
      }

      const openTime = formatTime(dayHours.open);
      const closeTime = formatTime(dayHours.close);
      const timeRange = `${openTime}-${closeTime}`;

      if (!currentGroup) {
        currentGroup = {
          days: [day],
          timeRange: timeRange,
        };
      } else if (currentGroup.timeRange === timeRange) {
        currentGroup.days.push(day);
      } else {
        groupedHours.push(currentGroup);
        currentGroup = {
          days: [day],
          timeRange: timeRange,
        };
      }
    });

    if (currentGroup) {
      groupedHours.push(currentGroup);
    }

    const formatDayRange = (days) => {
      if (days.length === 1) {
        return days[0].charAt(0).toUpperCase() + days[0].slice(1, 3);
      }
      const firstDay = days[0].charAt(0).toUpperCase() + days[0].slice(1, 3);
      const lastDay =
        days[days.length - 1].charAt(0).toUpperCase() +
        days[days.length - 1].slice(1, 3);
      return `${firstDay}-${lastDay}`;
    };

    let hoursContent = "";
    groupedHours.forEach((group) => {
      if (group.days.length > 0) {
        hoursContent += `${formatDayRange(group.days)}: ${group.timeRange}; `;
      }
    });

    return hoursContent.trim() || "Hours not available";
  }

  async init() {
    try {
      await this.loadStores();
      this.setupEventListeners();
      this.hideLoader();
    } catch (error) {
      console.error("Initialization failed:", error);
      this.showError("Failed to load stores. Please try again later.");
      this.hideLoader();
    }
  }

  hideLoader() {
    if (this.preloader) {
      this.preloader.style.opacity = "0";
      if (this.wrapper) {
        this.wrapper.style.opacity = "1";
        this.wrapper.style.visibility = "visible";
      }
      setTimeout(() => {
        document.body.classList.add("grid-loaded");
        this.preloader.style.display = "none";
      }, 300);
    }
  }

  async loadStores() {
    try {
      const response = await fetch(
        `${this.config.backendUrl}/api/stores?brand=${this.config.brand}`
      );
      const dbStores = response.ok ? await response.json() : [];

      // Filter out stores without map coordinates (latitude and longitude)
      const storesWithCoordinates = dbStores.filter(
        (store) =>
          store.latitude != null &&
          store.longitude != null &&
          !isNaN(parseFloat(store.latitude)) &&
          !isNaN(parseFloat(store.longitude))
      );

      this.allStores = storesWithCoordinates.map((store) =>
        this.formatStore(store)
      );

      // Sort alphabetically by default
      this.allStores.sort((a, b) => {
        const nameA = (a.name || "").toLowerCase();
        const nameB = (b.name || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });

      this.filteredStores = [...this.allStores];
      this.currentPage = 1;
      this.displayStores();
      this.updateStats();
    } catch (error) {
      console.error("Failed to fetch stores:", error);
      throw error;
    }
  }

  setupEventListeners() {
    // Search input
    const searchInput = document.getElementById(
      `store-search-${this.sectionId}`
    );
    const clearButton = document.querySelector(
      `#store-search-${this.sectionId} + .store-grid-search-clear`
    );

    if (searchInput) {
      searchInput.addEventListener("input", (e) => {
        this.searchTerm = e.target.value.toLowerCase().trim();
        this.applyFilters();
      });
    }

    if (clearButton) {
      clearButton.addEventListener("click", () => {
        this.searchTerm = "";
        if (searchInput) searchInput.value = "";
        this.applyFilters();
      });
    }

    // Province filter toggle
    const provinceToggle = document.querySelector(
      `.province-filter-toggle[data-section-id="${this.sectionId}"]`
    );
    if (provinceToggle) {
      provinceToggle.addEventListener("click", () => {
        const options = provinceToggle.nextElementSibling;
        const isExpanded =
          provinceToggle.getAttribute("aria-expanded") === "true";
        provinceToggle.setAttribute("aria-expanded", !isExpanded);
        options.setAttribute("aria-hidden", isExpanded);
        options.classList.toggle("visible", !isExpanded);
      });
    }

    // Province checkboxes
    const provinceCheckboxes = document.querySelectorAll(
      `.province-checkbox[data-section-id="${this.sectionId}"]`
    );
    provinceCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", () => {
        this.selectedProvinces = Array.from(
          document.querySelectorAll(
            `.province-checkbox[data-section-id="${this.sectionId}"]:checked`
          )
        ).map((cb) => cb.value);
        this.applyFilters();
      });
    });
  }

  applyFilters() {
    let filtered = [...this.allStores];

    // Apply province and country filter
    if (this.selectedProvinces.length > 0) {
      // Define which filters are countries (not South African provinces)
      const countryLevelFilters = [
        "Namibia",
        "Lesotho",
        "Eswatini",
        "Botswana",
      ];

      // Separate selected countries from selected provinces
      const selectedCountries = this.selectedProvinces.filter((selection) =>
        countryLevelFilters.includes(selection)
      );
      const selectedSaProvinces = this.selectedProvinces.filter(
        (selection) => !countryLevelFilters.includes(selection)
      );

      filtered = filtered.filter((store) => {
        // Show store if its country_name matches a selected country
        if (selectedCountries.includes(store.country_name)) {
          return true;
        }
        // Show store if its province matches a selected SA province
        if (selectedSaProvinces.includes(store.province)) {
          return true;
        }
        // Otherwise, exclude the store
        return false;
      });
    }

    // Apply search filter
    if (this.searchTerm) {
      filtered = filtered.filter((store) => {
        const searchFields = [
          store.name,
          store.address,
          store.contact_number,
          store.province,
          store.country_name,
        ]
          .join(" ")
          .toLowerCase();
        return searchFields.includes(this.searchTerm);
      });
    }

    // Sort alphabetically
    filtered.sort((a, b) => {
      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });

    this.filteredStores = filtered;
    this.currentPage = 1; // Reset to first page when filters change
    this.displayStores();
    this.updateStats();
  }

  displayStores() {
    const container = document.getElementById(
      `store-grid-container-${this.sectionId}`
    );
    const emptyMessage = document.getElementById(
      `store-grid-empty-${this.sectionId}`
    );

    if (!container) return;

    if (this.filteredStores.length === 0) {
      container.innerHTML = "";
      if (emptyMessage) emptyMessage.style.display = "block";
      this.renderPagination();
      this.updateStats();
      return;
    }

    if (emptyMessage) emptyMessage.style.display = "none";

    // Calculate pagination
    const totalPages = Math.ceil(
      this.filteredStores.length / this.storesPerPage
    );
    const startIndex = (this.currentPage - 1) * this.storesPerPage;
    const endIndex = startIndex + this.storesPerPage;
    const storesToDisplay = this.filteredStores.slice(startIndex, endIndex);

    container.innerHTML = storesToDisplay
      .map((store) => this.createStoreCard(store))
      .join("");

    // Add click handlers for Google Maps links
    container.querySelectorAll(".store-google-maps-link").forEach((link) => {
      link.addEventListener("click", (e) => {
        e.preventDefault();
        window.open(link.href, "_blank");
      });
    });

    // Render pagination
    this.renderPagination();

    // Update stats to show correct pagination range
    this.updateStats();
  }

  renderPagination() {
    const paginationContainer = document.getElementById(
      `store-grid-pagination-${this.sectionId}`
    );
    if (!paginationContainer) return;

    const totalPages = Math.ceil(
      this.filteredStores.length / this.storesPerPage
    );

    if (totalPages <= 1) {
      paginationContainer.style.display = "none";
      return;
    }

    paginationContainer.style.display = "flex";

    let paginationHTML = "";

    // Previous button
    paginationHTML += `
      <button 
        class="pagination-btn pagination-prev" 
        ${this.currentPage === 1 ? "disabled" : ""}
        data-page="${this.currentPage - 1}"
      >
        Previous
      </button>
    `;

    // Page numbers
    const maxVisiblePages = 7;
    let startPage = Math.max(
      1,
      this.currentPage - Math.floor(maxVisiblePages / 2)
    );
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      paginationHTML += `<button class="pagination-btn" data-page="1">1</button>`;
      if (startPage > 2) {
        paginationHTML += `<span class="pagination-ellipsis">...</span>`;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      paginationHTML += `
        <button 
          class="pagination-btn ${i === this.currentPage ? "active" : ""}" 
          data-page="${i}"
        >
          ${i}
        </button>
      `;
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        paginationHTML += `<span class="pagination-ellipsis">...</span>`;
      }
      paginationHTML += `<button class="pagination-btn" data-page="${totalPages}">${totalPages}</button>`;
    }

    // Next button
    paginationHTML += `
      <button 
        class="pagination-btn pagination-next" 
        ${this.currentPage === totalPages ? "disabled" : ""}
        data-page="${this.currentPage + 1}"
      >
        Next
      </button>
    `;

    paginationContainer.innerHTML = paginationHTML;

    // Add event listeners
    paginationContainer.querySelectorAll(".pagination-btn").forEach((btn) => {
      if (!btn.disabled) {
        btn.addEventListener("click", (e) => {
          const page = parseInt(e.target.dataset.page);
          if (page && page !== this.currentPage) {
            this.currentPage = page;
            this.displayStores();
            // Scroll to top of grid
            const container = document.getElementById(
              `store-grid-container-${this.sectionId}`
            );
            if (container) {
              container.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }
        });
      }
    });
  }

  createStoreCard(store) {
    const operatingHours = this.formatOperatingHours(store.operating_hours);

    return `
      <div class="store-card" data-store-id="${store.id}">
        <div class="store-card-header">
          <h3 class="store-card-name">${this.escapeHtml(store.name)}</h3>
        </div>
        <div class="store-card-body">
          <div class="store-card-detail">
            <strong>Address:</strong>
            <p>${this.escapeHtml(store.address || "N/A")}</p>
          </div>
          <div class="store-card-detail">
            <strong>Contact:</strong>
            <p>${this.escapeHtml(store.contact_number || "N/A")}</p>
          </div>
          <div class="store-card-detail">
            <strong>Province:</strong>
            <p>${this.escapeHtml(store.province || "N/A")}</p>
          </div>
          ${
            store.country_name
              ? `
          <div class="store-card-detail">
            <strong>Country:</strong>
            <p>${this.escapeHtml(store.country_name)}</p>
          </div>
          `
              : ""
          }
          <div class="store-card-detail">
            <strong>Operating Hours:</strong>
            <p>${this.escapeHtml(operatingHours)}</p>
          </div>
          ${
            store.status
              ? `
          <div class="store-card-detail">
            <strong>Status:</strong>
            <p class="store-status ${store.status
              .toLowerCase()
              .replace(/\s+/g, "-")}">${this.escapeHtml(store.status)}</p>
          </div>
          `
              : ""
          }
        </div>
        <div class="store-card-footer">
          ${
            store.google_maps_url
              ? `
          <a href="${store.google_maps_url}" 
             target="_blank" 
             class="store-google-maps-link">
            View on Google Maps
          </a>
          `
              : ""
          }
        </div>
      </div>
    `;
  }

  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  updateStats() {
    const statsElement = document.getElementById(
      `store-stats-${this.sectionId}`
    );
    if (statsElement) {
      const total = this.allStores.length;
      const filteredTotal = this.filteredStores.length;

      if (filteredTotal === 0) {
        statsElement.innerHTML = `<p>Showing 0 of ${total} stores</p>`;
        return;
      }

      // Calculate pagination range
      const startIndex = (this.currentPage - 1) * this.storesPerPage + 1;
      const endIndex = Math.min(
        startIndex + this.storesPerPage - 1,
        filteredTotal
      );

      statsElement.innerHTML = `<p>Showing ${startIndex}-${endIndex} of ${filteredTotal} stores</p>`;
    }
  }

  showError(message) {
    const container = document.getElementById(
      `store-grid-container-${this.sectionId}`
    );
    if (container) {
      container.innerHTML = `<div class="store-grid-error"><p>${message}</p></div>`;
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  Object.keys(window.storeGridConfig).forEach((sectionId) => {
    try {
      new StoreGrid(sectionId, window.storeGridConfig[sectionId]);
    } catch (error) {
      console.error(
        `Failed to initialize store grid for section ${sectionId}:`,
        error
      );
    }
  });
});
